/**
 * MidiBindings — runtime registry of "this controller knob → that
 * synth parameter" links, plus the Learn-mode handshake that lets
 * the user create one by wiggling a controller.
 *
 * Design:
 *
 *   - Bindings are addressed by **paramRef** strings, not by direct
 *     Parameter references. The string is stable across page reloads
 *     and works even when the Parameter instance gets recreated by a
 *     preset load. Paramrefs look like:
 *       module:filter:Cutoff
 *       macro:macro-1:Value
 *
 *   - The actual NumericParameter for each ref is provided by a
 *     `resolver` function the caller plugs in at construction. This
 *     keeps MidiBindings ignorant of the modules / macros / voice
 *     settings hierarchy — it just asks the resolver "what's at
 *     this ref right now?" and writes via `setNormalized`.
 *
 *   - Learn flow:
 *       1. UI calls `learn(paramRef)` — pending learn captured.
 *       2. Next CC message arrives → bind `(channel, controller) →
 *          paramRef`. Existing binding for that ref (or that
 *          controller) is overwritten.
 *       3. Future CC messages on that pair drive the resolved param
 *          via `setNormalized(value / 127, 'midi')`.
 *
 *   - Bindings serialize as a flat `[{ paramRef, channel, controller }]`
 *     list so presets can round-trip them through the schema.
 */

import type { MidiEngine } from './MidiEngine';
import type { NumericParameter } from '@ac-slayer/synth-core';

/** One persisted MIDI mapping. Channel is 0..15, controller 0..127. */
export interface MidiBinding {
  readonly paramRef: string;
  readonly channel: number;
  readonly controller: number;
}

/**
 * Resolves a paramRef string back to its live NumericParameter.
 * Returns null when the ref is unknown — for example, after a preset
 * load that removed a macro the binding pointed at. The bindings
 * store keeps the entry but no-ops on its CC writes.
 */
export type ParamResolver = (paramRef: string) => NumericParameter | null;

export type LearnState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending'; readonly paramRef: string };

export type BindingsListener = (
  bindings: readonly MidiBinding[],
  learn: LearnState,
) => void;

/**
 * Per-binding pickup state. Stored runtime-only (not persisted) so
 * a fresh page load starts in re-acquire mode for every binding —
 * the controller's hardware position is unknown until a message
 * arrives, and we'd rather not jump the knob to wherever it
 * happens to be sitting.
 */
interface PickupState {
  /** True once the controller has "caught up" to the param's
   *  current value and we're tracking. False until then. */
  active: boolean;
  /** Most recent normalized CC value (0..1). null until first
   *  message arrives. */
  lastCC: number | null;
  /** What we wrote to the param last — used to detect if the user
   *  manually moved the knob between messages, in which case
   *  pickup re-acquires. */
  lastWritten: number | null;
}

/**
 * Threshold for "the knob moved on its own" detection. Param values
 * inevitably drift by floating-point epsilon as they round-trip
 * through Parameter.set; > 0.001 (0.1% of range) is comfortably
 * above that and well below any drag a user would make manually.
 */
const PICKUP_EPSILON = 0.001;

export class MidiBindings {
  private resolver: ParamResolver;
  private bindings: MidiBinding[] = [];
  /** Quick lookup: "${channel}:${controller}" → paramRef. */
  private cellIndex = new Map<string, string>();
  /** Per-paramRef pickup state — keys parallel `bindings.paramRef`.
   *  Cleared on unbind / learn / applyBindings; re-initialized
   *  whenever a binding is created. */
  private pickupState = new Map<string, PickupState>();
  private learnState: LearnState = { kind: 'idle' };
  private readonly listeners = new Set<BindingsListener>();
  private readonly unsubMidi: () => void;

  constructor(midi: MidiEngine, resolver: ParamResolver) {
    this.resolver = resolver;
    this.unsubMidi = midi.onMessage((msg) => this.handleMessage(msg));
  }

  /**
   * Replace the resolver — used after a preset load that recreated
   * effect instances with the same ids. The bindings array is
   * left intact; existing entries just resolve through the new
   * lookup function.
   */
  setResolver(resolver: ParamResolver): void {
    this.resolver = resolver;
  }

  list(): readonly MidiBinding[] {
    return this.bindings;
  }

  /** The binding for a given paramRef, or null when none. */
  forParam(paramRef: string): MidiBinding | null {
    return this.bindings.find((b) => b.paramRef === paramRef) ?? null;
  }

  getLearnState(): LearnState {
    return this.learnState;
  }

  subscribe(fn: BindingsListener): () => void {
    this.listeners.add(fn);
    fn(this.bindings, this.learnState);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Enter Learn mode for `paramRef`. The next CC message will create
   * the binding. If a learn is already pending for a different ref,
   * the new request supersedes it — clicking "Learn" on knob B after
   * starting on knob A discards A's pending state, which is what
   * users expect.
   */
  learn(paramRef: string): void {
    this.learnState = { kind: 'pending', paramRef };
    this.emit();
  }

  /** Cancel any in-flight learn request. */
  cancelLearn(): void {
    if (this.learnState.kind === 'idle') return;
    this.learnState = { kind: 'idle' };
    this.emit();
  }

  /** Remove the binding for `paramRef`, if any. */
  unbind(paramRef: string): void {
    const idx = this.bindings.findIndex((b) => b.paramRef === paramRef);
    if (idx < 0) return;
    const removed = this.bindings[idx]!;
    this.bindings.splice(idx, 1);
    this.cellIndex.delete(cellKey(removed.channel, removed.controller));
    this.pickupState.delete(paramRef);
    this.emit();
  }

  /** Wholesale replace bindings — used by preset load. Pickup state
   *  is cleared so every restored binding starts in re-acquire mode;
   *  the user's hardware is at whatever position it happened to be
   *  in when the preset loaded, and the on-screen values are now
   *  whatever the preset dictates — not necessarily aligned. */
  applyBindings(bindings: readonly MidiBinding[]): void {
    this.bindings = bindings.map((b) => ({
      paramRef: b.paramRef,
      channel: b.channel,
      controller: b.controller,
    }));
    this.cellIndex.clear();
    this.pickupState.clear();
    for (const b of this.bindings) {
      this.cellIndex.set(cellKey(b.channel, b.controller), b.paramRef);
      this.pickupState.set(b.paramRef, {
        active: false,
        lastCC: null,
        lastWritten: null,
      });
    }
    if (this.learnState.kind !== 'idle') this.learnState = { kind: 'idle' };
    this.emit();
  }

  /** Snapshot for preset capture. Order is stable across calls so
   *  comparing two captures is meaningful. */
  serialize(): MidiBinding[] {
    return this.bindings.map((b) => ({ ...b }));
  }

  dispose(): void {
    this.unsubMidi();
    this.listeners.clear();
    this.pickupState.clear();
  }

  // ── Internals ───────────────────────────────────────────────────

  private handleMessage(msg: { type: string }): void {
    if (msg.type !== 'cc') return;
    const cc = msg as {
      type: 'cc';
      channel: number;
      controller: number;
      value: number;
    };

    // Learn-mode interception. A pending learn binds the next CC we
    // see, regardless of whether it was already mapped — overwriting
    // is the user's intent (they want THIS controller for this knob).
    if (this.learnState.kind === 'pending') {
      this.bindLearned(this.learnState.paramRef, cc.channel, cc.controller);
      this.learnState = { kind: 'idle' };
      this.emit();
      // Don't dispatch the value on the same message that created the
      // binding — feels less surprising than the knob jumping the
      // moment the user wiggles to learn.
      return;
    }

    // Normal dispatch — with pickup mode (a.k.a. "soft takeover") so
    // the on-screen knob doesn't jump to wherever the controller's
    // hardware happens to be sitting. The knob only starts following
    // the controller once the controller's value has CROSSED the
    // knob's current value; before that, incoming CCs are recorded
    // but not written.
    const ref = this.cellIndex.get(cellKey(cc.channel, cc.controller));
    if (!ref) return;
    const param = this.resolver(ref);
    if (!param) return;

    const incoming = cc.value / 127;
    const current = param.normalized;
    let state = this.pickupState.get(ref);
    if (!state) {
      // Defensive — every binding should have state, but resync if
      // not. Treat as fresh (not yet active).
      state = { active: false, lastCC: null, lastWritten: null };
      this.pickupState.set(ref, state);
    }

    // Detect manual knob movement since our last write — if the user
    // has dragged the knob, the controller is no longer in sync and
    // we should re-acquire instead of snapping to its position.
    if (
      state.active &&
      state.lastWritten !== null &&
      Math.abs(current - state.lastWritten) > PICKUP_EPSILON
    ) {
      state.active = false;
      state.lastCC = null;
    }

    if (state.active) {
      // Already tracking — write through.
      param.setNormalized(incoming, 'midi');
      state.lastCC = incoming;
      state.lastWritten = incoming;
      return;
    }

    // Not yet tracking. First message: just record so we know the
    // controller's starting position; don't write anything.
    if (state.lastCC === null) {
      state.lastCC = incoming;
      return;
    }

    // Subsequent messages: check whether the controller's path
    // (lastCC → incoming) has crossed the param's current value.
    // If so, take over from this point forward.
    const min = Math.min(state.lastCC, incoming);
    const max = Math.max(state.lastCC, incoming);
    if (current >= min && current <= max) {
      state.active = true;
      param.setNormalized(incoming, 'midi');
      state.lastWritten = incoming;
    }
    state.lastCC = incoming;
  }

  private bindLearned(paramRef: string, channel: number, controller: number): void {
    // Drop any existing binding pointing at the same paramRef OR using
    // the same (channel, controller) cell. Either collision means the
    // user is reusing a knob; the new wiring should win.
    const existingByRef = this.bindings.findIndex((b) => b.paramRef === paramRef);
    if (existingByRef >= 0) {
      const removed = this.bindings.splice(existingByRef, 1)[0]!;
      this.cellIndex.delete(cellKey(removed.channel, removed.controller));
    }
    const existingCell = this.cellIndex.get(cellKey(channel, controller));
    if (existingCell !== undefined) {
      const cellIdx = this.bindings.findIndex((b) => b.paramRef === existingCell);
      if (cellIdx >= 0) this.bindings.splice(cellIdx, 1);
      this.cellIndex.delete(cellKey(channel, controller));
    }
    const next: MidiBinding = { paramRef, channel, controller };
    this.bindings.push(next);
    this.cellIndex.set(cellKey(channel, controller), paramRef);
    // Fresh pickup state — controller's current hardware position
    // is unknown so we wait for the next CC to learn it before
    // doing anything.
    this.pickupState.set(paramRef, {
      active: false,
      lastCC: null,
      lastWritten: null,
    });
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this.bindings, this.learnState);
  }
}

function cellKey(channel: number, controller: number): string {
  return `${channel}:${controller}`;
}

/**
 * Build a paramRef for a module parameter. Format must stay stable
 * across versions — bumping the schema requires a migration.
 */
export function moduleParamRef(moduleId: string, paramName: string): string {
  return `module:${moduleId}:${paramName}`;
}
